import { DataTable } from '../DataTable';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '../ui/badge';
import { Breadcrumbs } from '../ui/breadcrumbs';
import { AdminAction, ADMIN_ACTION_TYPES } from '@shiftly/shared/src/schema';
import type { PaginatedResponse } from '../../lib/supabase/admin-actions/supabaseAdminActionService';
import { useAdminActions, useAllAdminActions } from '../../lib/supabase/admin-actions/supabaseAdminActionService';
import { cn } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Check, ChevronLeft, ChevronRight, ChevronsUpDown, Info, Search } from 'lucide-react';
import { useUsers } from '../../lib/supabase/users/supabaseUserService';
import { useModels } from '../../lib/supabase/models/supabaseModelService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../ui/tooltip";
import { useRoles } from '../../lib/supabase/roles/supabaseRoleService';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';
import { DateTime } from 'luxon';

const AuditLogsView = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedRow, setSelectedRow] = useState<AdminAction & { id: string } | null>(null);
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [selectedAction, setSelectedAction] = useState<string>('all');
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [userPopoverOpen, setUserPopoverOpen] = useState(false);
  const [actionPopoverOpen, setActionPopoverOpen] = useState(false);
  const { data, isLoading } = useAdminActions(pageIndex + 1, selectedUser, selectedAction, searchTerm);
  const { data: users } = useUsers();
  const { data: models } = useModels();
  const { data: roles } = useRoles();
  const { data: allActions } = useAllAdminActions();

  useEffect(() => {
    setPageIndex(0);
  }, [selectedUser, selectedAction, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Use action types from the schema
  const actionTypes = ADMIN_ACTION_TYPES;

  // Get unique users who have audit entries from all actions
  const usersWithEntries = allActions ? [...new Set(allActions.map((item: AdminAction & { id: string }) => item.performed_by))] : [];

  // No need for filteredData anymore since filtering is done in the hook
  const filteredData = data?.data || [];

  const getUserDisplayName = (user_id: string) => {
    if (user_id === 'system') return 'System';
    const user = users?.find(u => u.id === user_id);
    return user?.full_name || "Deleted User";
  };

  const getActionBadgeClassName = (type: string) => {
    if (type.startsWith('CREATE_')) return 'bg-brand-green text-brand-dark border-brand-green dark:bg-brand-dark/20 dark:text-brand-green dark:border-brand-border/50';
    if (type.startsWith('EDIT_')) return 'bg-brand-gold text-brand-dark border-brand-gold dark:bg-brand-dark/20 dark:text-brand-gold dark:border-brand-border/50';
    if (type.startsWith('DELETE_')) return 'bg-brand-red text-brand-dark border-brand-red dark:bg-brand-dark/20 dark:text-brand-red dark:border-brand-border/50';
    return 'bg-brand-gold text-brand-dark border-brand-gold dark:bg-brand-dark/20 dark:text-brand-gold dark:border-brand-border/50';
  };

  const formatChangeValue = (value: any, key?: string): string => {
    if (value === null) return 'None';
    if (value === undefined) return 'Not set';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'string') {
      if (key === 'model_id') {
        const model = models?.find(m => m.id === value);
        return `${model?.name} (${value})` || value;
      }
      if (key === 'user_id' || key === 'performed_by' || key === 'verified_by' || key === 'trainer_id') {
        const user = users?.find(u => u.id === value);
        return `${user?.full_name} (${value})` || value;
      }
      return value;
    }
    if (Array.isArray(value)) {
      // If this is a roles array, convert IDs to names
      if (key === 'roles') {
        const roleNames = value.map(roleId => {
          const role = roles?.find(r => r.id === roleId);
          return role?.name || roleId;
        });
        return roleNames.join(', ');
      }
      // If this is a warnings array, show the messages
      if (key === 'warnings') {
        return value.map((w: any) => w.message).join(', ');
      }
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return Object.entries(value)
        .map(([key, val]) => `${key}: ${formatChangeValue(val, key)}`)
        .join(', ');
    }
    return String(value);
  };

  const formatChanges = (changes: { before?: any; after?: any }) => {
    const formattedChanges: string[] = [];

    if (changes.before && changes.after) {
      // Compare before and after
      Object.entries(changes.after).forEach(([key, afterValue]) => {
        const beforeValue = changes.before[key];
        if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
          if (key === 'warnings') {
            const beforeWarnings = (beforeValue as any[]) || [];
            const afterWarnings = (afterValue as any[]) || [];

            if (beforeWarnings.length === 0 && afterWarnings.length > 0) {
              formattedChanges.push('Added warnings:');
              afterWarnings.forEach(w => {
                formattedChanges.push(`  + "${w.message}"`);
              });
            } else if (beforeWarnings.length > 0 && afterWarnings.length === 0) {
              formattedChanges.push('Removed warnings:');
              beforeWarnings.forEach(w => {
                formattedChanges.push(`  - "${w.message}"`);
              });
            } else {
              formattedChanges.push(
                `${key}: ${formatChangeValue(beforeValue, key)} → ${formatChangeValue(afterValue, key)}`
              );
            }
          } else {
            formattedChanges.push(
              `${key}: ${formatChangeValue(beforeValue, key)} → ${formatChangeValue(afterValue, key)}`
            );
          }
        }
      });
    } else if (changes.before) {
      // Deletion
      Object.entries(changes.before).forEach(([key, value]) => {
        if (key === 'warnings') {
          const warnings = (value as any[]) || [];
          formattedChanges.push('Removed warnings:');
          warnings.forEach(w => {
            formattedChanges.push(`  - "${w.message}"`);
          });
        } else {
          formattedChanges.push(`${key}: ${formatChangeValue(value, key)} (removed)`);
        }
      });
    } else if (changes.after) {
      // Creation
      Object.entries(changes.after).forEach(([key, value]) => {
        if (key === 'warnings') {
          const warnings = (value as any[]) || [];
          formattedChanges.push('Added warnings:');
          warnings.forEach(w => {
            formattedChanges.push(`  + "${w.message}"`);
          });
        } else {
          formattedChanges.push(`${key}: ${formatChangeValue(value, key)} (added)`);
        }
      });
    }

    return formattedChanges;
  };

  const formatActionType = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const columns: ColumnDef<AdminAction & { id: string }, any>[] = [
    {
      accessorKey: 'timestamp',
      header: 'Time',
      size: 250,
      minSize: 250,
      cell: ({ row }) => {
        const timestamp = row.original.timestamp;

        const utcDate =
          typeof timestamp === 'number'
            ? DateTime.fromMillis(timestamp, { zone: 'utc' })
            : timestamp instanceof Date
              ? DateTime.fromJSDate(timestamp, { zone: 'utc' })
              : timestamp;

        return (
          <div className="flex flex-col min-w-0 whitespace-nowrap">
            <span className="font-medium">{utcDate.toFormat('MMM d, yyyy')}</span>
            <span className="text-sm text-muted-foreground">{utcDate.toFormat('HH:mm:ss')} UTC</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Action',
      size: 140,
      cell: ({ row }) => (
        <Badge
          className={cn(
            "capitalize truncate",
            getActionBadgeClassName(row.original.type)
          )}
        >
          {row.original.type.toLowerCase().replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      accessorKey: 'performedBy',
      header: 'User',
      size: 220,
      cell: ({ row }) => {
        const user_id = row.original.performed_by;
        return (
          <div className="flex flex-col min-w-0">
            <span className="truncate">{user_id ? getUserDisplayName(user_id) : 'SYSTEM'}</span>
            {user_id !== 'system' && (
              <span className="text-xs text-muted-foreground font-mono truncate">{user_id}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'targetType',
      header: 'Resource',
      size: 180,
      cell: ({ row }) => (
        <div className="flex flex-col gap-1 min-w-0">
          <Badge variant="secondary" className="capitalize w-fit truncate">{row.original.target_type}</Badge>
          {row.original.target_id && (
            <span className="ml-0.5 text-xs text-muted-foreground font-mono truncate">{row.original.target_id}</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'details',
      header: 'Details',
      size: 500,
      maxSize: 600,
      cell: ({ row }) => {
        const { details } = row.original;
        // Parse the details string to find user IDs and model IDs and replace them with full names
        const formattedDetails = details.replace(/([a-zA-Z0-9]{20,})/g, (match) => {
          // First check if it's a user ID
          const user = users?.find(u => u.id === match);
          if (user) {
            return user.full_name;
          }

          // Then check if it's a model ID
          const model = models?.find(m => m.id === match);
          if (model) {
            return model.name;
          }

          // If neither, return "Deleted User" for backward compatibility
          return "Deleted User";
        });
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="font-medium truncate cursor-help max-w-[400px]">
                {formattedDetails}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-md">
              <p className="whitespace-pre-wrap">{formattedDetails}</p>
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 50,
      cell: () => {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-4 w-4 justify-self-center text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Click on a row to view changes</p>
            </TooltipContent>
          </Tooltip>
        );
      },
    }
  ];

  const paginatedData = data as PaginatedResponse<AdminAction & { id: string }> | undefined;
  const totalPages = paginatedData ? Math.ceil(paginatedData.totalCount / 10) : 0;

  const SkeletonRow = () => (
    <tr className="border-b">
      <td className="p-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-20" />
        </div>
      </td>
      <td className="p-4">
        <Skeleton className="h-6 w-28" />
      </td>
      <td className="p-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </td>
      <td className="p-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-3 w-28" />
        </div>
      </td>
      <td className="p-4">
        <Skeleton className="h-4 w-64" />
      </td>
      <td className="p-4">
        <Skeleton className="h-4 w-4 mx-auto" />
      </td>
    </tr>
  );

  const breadcrumbs = [
    { title: 'Home', href: '/' },
    { title: 'Audit Logs', href: '/audit-logs' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="space-y-1">
          <Breadcrumbs items={breadcrumbs} className="mb-2" />
          <h1 className="text-3xl font-bold tracking-tight text-brand-dark dark:text-foreground">
            Audit Logs
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground">
            View the audit logs for all actions performed on the system
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="relative w-full sm:w-[300px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search logs..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <Popover open={userPopoverOpen} onOpenChange={setUserPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={userPopoverOpen}
                className="w-full sm:w-[220px] justify-between"
              >
                {selectedUser === 'all' ? 'All Users' :
                  selectedUser === 'system' ? 'System' :
                    users?.find(u => u.id === selectedUser)?.full_name || "Deleted User"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search users..." />
                <CommandList>
                  <CommandEmpty>No user found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setSelectedUser('all');
                        setUserPopoverOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedUser === 'all' ? "opacity-100" : "opacity-0"
                        )}
                      />
                      All Users
                    </CommandItem>
                    {usersWithEntries.map(user_id => {
                      const user = users?.find(u => u.id === user_id);
                      const displayName = !user_id ? 'SYSTEM' : user?.full_name || "Deleted User";
                      return (
                        <CommandItem
                          key={user_id}
                          onSelect={() => {
                            setSelectedUser(user_id || 'system');
                            setUserPopoverOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedUser === user_id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {displayName}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <Popover open={actionPopoverOpen} onOpenChange={setActionPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={actionPopoverOpen}
                className="w-full sm:w-[220px] justify-between"
              >
                {selectedAction === 'all' ? 'All Actions' : formatActionType(selectedAction)}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search actions..." />
                <CommandList>
                  <CommandEmpty>No action found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setSelectedAction('all');
                        setActionPopoverOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedAction === 'all' ? "opacity-100" : "opacity-0"
                        )}
                      />
                      All Actions
                    </CommandItem>
                    {actionTypes.map(type => (
                      <CommandItem
                        key={type}
                        onSelect={() => {
                          setSelectedAction(type);
                          setActionPopoverOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedAction === type ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {formatActionType(type)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div>
        {isLoading ? (
          <div className="overflow-x-auto">
            <div className="rounded-xl border bg-white dark:bg-brand-dark/50 dark:border-brand-border dark:shadow-black/10">
              <table className="w-full">
                <thead className="bg-muted/50 dark:bg-brand-dark/30">
                  <tr className="border-b dark:border-brand-border">
                    <th className="p-4 text-left text-sm font-medium">Time</th>
                    <th className="p-4 text-left text-sm font-medium">Action</th>
                    <th className="p-4 text-left text-sm font-medium">User</th>
                    <th className="p-4 text-left text-sm font-medium">Resource</th>
                    <th className="p-4 text-left text-sm font-medium">Details</th>
                    <th className="p-4 text-left text-sm font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <SkeletonRow key={i} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (!filteredData || filteredData.length === 0) ? (
          <Card>
            <CardContent className="text-center py-6">
              <p className="text-muted-foreground dark:text-muted-foreground">No audit logs to show</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="overflow-x-auto">
              <DataTable
                columns={columns}
                data={filteredData}
                initialSorting={[{ id: 'timestamp', desc: true }]}
                tableId="audit-logs"
                searchPlaceholder="Search audit logs..."
                hideFooter={true}
                useSearch={false}
                onRowClick={(row: AdminAction & { id: string }) => setSelectedRow(row)}
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-b-xl border-x border-b bg-white dark:bg-brand-dark/50 dark:border-brand-border dark:shadow-black/10">
              <div className="flex items-center gap-4 text-sm text-muted-foreground dark:text-muted-foreground">
                <span>
                  Page {pageIndex + 1} of {totalPages}
                </span>
                <span>
                  Showing {filteredData.length} of {data?.totalCount || 0} entries
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPageIndex(prev => Math.max(0, prev - 1))}
                  disabled={pageIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPageIndex(prev => Math.min(totalPages - 1, prev + 1))}
                  disabled={pageIndex === totalPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={!!selectedRow} onOpenChange={(open) => !open && setSelectedRow(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Change Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedRow?.changes && (
              <div className="bg-muted rounded-lg p-4">
                <div className="space-y-2">
                  {formatChanges(selectedRow.changes).map((change, index) => (
                    <div key={index} className="text-sm font-mono">
                      {change}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogsView; 